import type { PreAuthRouteDecision } from '@rapid-recovery-agency-inc/insightt-graphql';

import {
  beginPreAuthAttempt as beginPreAuthAttemptAction,
  beginSession as beginSessionAction,
  setPreAuthAttemptDecision,
  setPreAuthAttemptStatus,
  setSessionStatus,
} from './auth-slice';
// NOTE: `./types` is imported with an explicit `.ts` extension — this directory also has a
// legacy `types.js` (unrelated `SET_DEVICE_TOKEN_REQUEST` action type, consumed by `actions.js`).
// Vite/vitest's default module resolution order checks `.js` before `.ts`, so the extensionless
// specifier would silently resolve to `types.js` at runtime. See constants.ts for the same note.
import { AuthState, DashboardSessionStatus, PreAuthAttemptStatus } from './types.ts';

/**
 * Latest-generation state transitions and reusable single-flight episode primitives (T016).
 *
 * This module sits between T014's generation-guarded Redux `auth` slice and every consumer that
 * starts an async authentication operation (T018's `usePreAuthRoute`, T022's OIDC client, T027's
 * silent-renewal episode, and the boundary's cleanup transaction). It owns two reusable
 * primitives:
 *
 * 1. {@link LatestGenerationEpisode} — starts a new "latest generation" for a family of
 *    operations (pre-authentication attempt, Dashboard session) by dispatching the slice's
 *    `begin*` action, reading back the authoritative generation the reducer assigned, and handing
 *    the caller an `AbortSignal` plus an `isCurrent()` check. A newer `begin()` call aborts the
 *    previous handle's signal immediately, so in-flight work for the old generation can stop
 *    before it ever tries to touch Redux (data-model.md §3 "a newer generation invalidates older
 *    completions"; §7 "[session generation is] invalidated on ... replacement"). The Redux slice
 *    itself also rejects any stale-generation payload (belt-and-suspenders), but callers should
 *    still check `isCurrent()`/`signal.aborted` before doing navigation or other non-Redux
 *    side effects, since the reducer guard alone cannot stop those.
 *
 * 2. {@link SingleFlightEpisode} — a generic single-flight helper: while an operation started by
 *    `run()` is in flight, a second `run()` call returns the *same* promise instead of starting a
 *    new operation (FR-032 "concurrent work MUST be deduplicated or safely cancelled"; data-model
 *    §8/§9 "[shared/one] promise ... [a]ll waiting requests receive the same outcome"). It is
 *    generic and holds no auth-specific knowledge, so T027's renewal episode and the boundary's
 *    cleanup transaction can each hold their own instance instead of reimplementing dedup.
 *
 * Neither primitive performs any network/GraphQL/OIDC work itself — that remains T018's,
 * T022's, and T027's responsibility. This module only supplies the generation/dedup machinery
 * and the small set of generation-guarded dispatch helpers those consumers call into.
 */

/** Minimal store shape this module needs — satisfied by a real Redux store or a test double. */
export interface AuthControllerStore {
  dispatch: (action: { type: string; payload?: unknown }) => unknown;
  getState: () => { auth: AuthState };
}

/** Handle returned by {@link LatestGenerationEpisode.begin}. */
export interface GenerationHandle {
  /** The authoritative generation the Redux reducer assigned to this operation. */
  readonly generation: number;
  /** Aborted as soon as a newer generation begins; pass to cancellable request work. */
  readonly signal: AbortSignal;
  /** True only while this handle's generation is still the latest one in the store. */
  isCurrent(): boolean;
}

/**
 * Starts and tracks the "latest generation" for one family of operations (e.g. pre-authentication
 * attempts, or the Dashboard session lifecycle). Wraps a Redux slice `begin*` action — which is
 * itself the source of truth for the generation counter — with cancellation of the previous
 * generation's in-flight work.
 */
export class LatestGenerationEpisode {
  private abortController: AbortController | null = null;

  constructor(
    private readonly store: AuthControllerStore,
    private readonly beginAction: () => { type: string; payload?: unknown },
    private readonly selectGeneration: (state: { auth: AuthState }) => number,
  ) {}

  /**
   * Begins a new latest generation: aborts the previous handle's signal (if any), dispatches the
   * slice's `begin*` action, and returns a handle bound to the generation the reducer assigned.
   */
  begin(): GenerationHandle {
    this.abortController?.abort();
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.store.dispatch(this.beginAction());
    const generation = this.selectGeneration(this.store.getState());

    return {
      generation,
      signal,
      isCurrent: () => this.selectGeneration(this.store.getState()) === generation,
    };
  }

  /** The generation currently considered latest, without starting a new one. */
  currentGeneration(): number {
    return this.selectGeneration(this.store.getState());
  }
}

/**
 * Generic single-flight helper (FR-032; data-model.md §8/§9). While one `run()`-started operation
 * is in flight, every concurrent `run()` call receives the *same* promise instead of starting a
 * duplicate operation. Holds no auth-specific knowledge — reusable for any operation kind
 * (silent-token renewal, session cleanup, or future episodes).
 */
export class SingleFlightEpisode<T> {
  private inFlight: Promise<T> | null = null;

  /**
   * Runs `operation` if none is in flight, otherwise returns the in-flight promise unchanged.
   * The slot clears itself once the running promise settles (success or failure) so a later call
   * starts a fresh operation.
   */
  run(operation: () => Promise<T>): Promise<T> {
    if (this.inFlight) {
      return this.inFlight;
    }

    const promise = operation().finally(() => {
      if (this.inFlight === promise) {
        this.inFlight = null;
      }
    });
    this.inFlight = promise;
    return promise;
  }

  /** True while an operation started by `run()` has not yet settled. */
  get isActive(): boolean {
    return this.inFlight !== null;
  }
}

/** Selects the current pre-authentication attempt generation from root state. */
const selectPreAuthAttemptGeneration = (state: { auth: AuthState }): number =>
  state.auth.preAuthAttempt.generation;

/** Selects the current Dashboard session generation from root state. */
const selectSessionGeneration = (state: { auth: AuthState }): number => state.auth.session.generation;

/**
 * Settlement outcome of one single-flight silent-renewal episode (T027; research.md §9;
 * data-model.md §8 "IDLE → ATTEMPTING → SUCCEEDED | INTERACTIVE_REQUIRED"). Every waiter on the
 * shared {@link AuthController.renewalEpisode} promise receives exactly one of these — never a
 * thrown/rejected promise — so a blocked or failed silent attempt is a normal settled value the
 * boundary/callers branch on, not an exception to catch (FR-032 "partial failures MUST end in a
 * deterministic ... state").
 *
 * `'success'`: a fresh token is now available via the boundary (`getAccessToken()`). This value
 * carries no token itself — the boundary's private envelope is the only place a fresh credential
 * is published to, per data-model.md §6.
 *
 * `'interaction_required'`: the one allowed `prompt=none` attempt failed, was rejected, or was
 * blocked (e.g. third-party-cookie restrictions) — or a fresh outcome could not be safely applied
 * because a different session generation became current while the attempt was still in flight.
 * The caller MUST route to user-controlled interactive login/recovery, never retry silently, and
 * never trigger an automatic top-level redirect from this layer (research.md §9).
 */
export type RenewalOutcome = { readonly outcome: 'success' } | { readonly outcome: 'interaction_required' };

/**
 * The T016 authentication controller: composes the two reusable primitives above with T014's
 * `auth` slice for the two generation-tracked entities (pre-authentication attempt, Dashboard
 * session) and exposes empty-by-default single-flight episodes for renewal/cleanup that later
 * tasks (T017, T027) attach real work to.
 */
export interface AuthController {
  /** Starts a new latest-generation pre-authentication attempt; supersedes any prior one. */
  beginPreAuthAttempt(): GenerationHandle;
  /**
   * Applies a status transition for `handle`'s generation. No-op (and never dispatches) once a
   * newer generation has started, so a stale/cancelled operation can never touch Redux state.
   */
  applyPreAuthAttemptStatus(handle: GenerationHandle, status: PreAuthAttemptStatus): void;
  /**
   * Records the authoritative route decision for `handle`'s generation. No-op once stale, for the
   * same reason as {@link applyPreAuthAttemptStatus}.
   */
  applyPreAuthAttemptDecision(handle: GenerationHandle, decision: PreAuthRouteDecision | null): void;
  /** Starts a new latest-generation Dashboard session lifecycle. */
  beginSession(): GenerationHandle;
  /** Applies a session status transition for `handle`'s generation; no-op once stale. */
  applySessionStatus(handle: GenerationHandle, status: DashboardSessionStatus): void;
  /**
   * Shared single-flight episode for the silent-renewal operation (data-model.md §8; T027). Typed
   * to {@link RenewalOutcome} (rather than the `cleanupEpisode`'s erased `unknown`) because T027
   * defines exactly one real caller/shape for this slot — `auth-boundary.ts`'s
   * `ensureFreshSession()` — so there is no other consumer that needs the generic left open.
   */
  readonly renewalEpisode: SingleFlightEpisode<RenewalOutcome>;
  /** Shared single-flight episode for the cleanup/logout transaction (data-model.md §9; T017). */
  readonly cleanupEpisode: SingleFlightEpisode<unknown>;
}

/** Creates a T016 {@link AuthController} bound to `store` (a real Redux store, or a test double). */
export function createAuthController(store: AuthControllerStore): AuthController {
  const preAuthAttemptGenerationEpisode = new LatestGenerationEpisode(
    store,
    beginPreAuthAttemptAction,
    selectPreAuthAttemptGeneration,
  );
  const sessionGenerationEpisode = new LatestGenerationEpisode(store, beginSessionAction, selectSessionGeneration);

  return {
    beginPreAuthAttempt: () => preAuthAttemptGenerationEpisode.begin(),

    applyPreAuthAttemptStatus: (handle, status) => {
      if (!handle.isCurrent()) {
        return;
      }
      store.dispatch(setPreAuthAttemptStatus({ generation: handle.generation, status }));
    },

    applyPreAuthAttemptDecision: (handle, decision) => {
      if (!handle.isCurrent()) {
        return;
      }
      store.dispatch(setPreAuthAttemptDecision({ generation: handle.generation, decision }));
    },

    beginSession: () => sessionGenerationEpisode.begin(),

    applySessionStatus: (handle, status) => {
      if (!handle.isCurrent()) {
        return;
      }
      store.dispatch(setSessionStatus({ generation: handle.generation, status }));
    },

    renewalEpisode: new SingleFlightEpisode<RenewalOutcome>(),
    cleanupEpisode: new SingleFlightEpisode<unknown>(),
  };
}
